/*
 * Axiom typo-proof search (theme-only, no app).
 *
 * Adds spelling tolerance on top of Shopify's native search, which only does
 * prefix/token matching (so "silverdo bearign" finds nothing). Strategy:
 *   - Build a vocabulary from the store's own products (titles/type/vendor/tags/SKU)
 *     via /products.json, cached in sessionStorage.
 *   - Correct only genuinely-misspelled query tokens (Damerau/OSA edit distance),
 *     leaving valid words, mid-typing prefixes, and part numbers alone.
 *   - Header predictive dropdown: if the RAW query returns no results, retry with the
 *     corrected query and show a "Showing results for ..." note. Never overrides a
 *     query that already found something.
 *   - Search results page: on a true 0-results page, redirect to the corrected query
 *     with a "Showing results for X / search instead for Y" banner.
 *
 * Fails safe: if /products.json is unavailable the vocabulary is empty and search
 * behaves exactly as before.
 */
(function () {
  'use strict';
  if (window.__axiomTypoSearch) return;
  window.__axiomTypoSearch = true;

  var VOCAB_KEY = 'axiom_search_vocab_v1';
  var VOCAB_TTL = 1000 * 60 * 60 * 6; // 6 hours
  var MIN_TOKEN = 2;
  var MAX_PAGES = 4; // products.json safety cap (250/page)

  function predictiveUrl() {
    return (window.routes && window.routes.predictive_search_url) || '/search/suggest';
  }
  function searchBase() {
    return (window.routes && window.routes.search_url) || '/search';
  }

  /* ------------------------------------------------------------------ vocab */
  var VOCAB = null;        // { freq:{token:count}, tokens:[...] }
  var vocabPromise = null;

  function tokenize(text) {
    return String(text || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .split(' ')
      .filter(function (t) { return t.length >= MIN_TOKEN; });
  }

  function buildIndex(freq) {
    return { freq: freq, tokens: Object.keys(freq) };
  }

  function loadVocab() {
    if (VOCAB) return Promise.resolve(VOCAB);
    if (vocabPromise) return vocabPromise;

    try {
      var raw = sessionStorage.getItem(VOCAB_KEY);
      if (raw) {
        var parsed = JSON.parse(raw);
        if (parsed && parsed.t && (Date.now() - parsed.ts) < VOCAB_TTL) {
          VOCAB = buildIndex(parsed.t);
          return Promise.resolve(VOCAB);
        }
      }
    } catch (e) {}

    vocabPromise = fetchVocab()
      .then(function (freq) {
        try { sessionStorage.setItem(VOCAB_KEY, JSON.stringify({ ts: Date.now(), t: freq })); } catch (e) {}
        VOCAB = buildIndex(freq);
        return VOCAB;
      })
      .catch(function () { VOCAB = buildIndex({}); return VOCAB; });
    return vocabPromise;
  }

  function fetchVocab() {
    var freq = {};
    function add(text, weight) {
      tokenize(text).forEach(function (tk) { freq[tk] = (freq[tk] || 0) + (weight || 1); });
    }
    function page(p) {
      return fetch('/products.json?limit=250&page=' + p, { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : { products: [] }; })
        .then(function (data) {
          var prods = (data && data.products) || [];
          prods.forEach(function (pr) {
            add(pr.title, 4);
            add(pr.product_type, 2);
            add(pr.vendor, 1);
            (pr.tags || []).forEach(function (t) { add(t, 1); });
            (pr.variants || []).forEach(function (v) { if (v.sku) add(v.sku, 1); });
          });
          if (prods.length === 250 && p < MAX_PAGES) return page(p + 1);
          return freq;
        });
    }
    return page(1);
  }

  /* ----------------------------------------------------- edit distance (OSA) */
  // Optimal String Alignment distance (Levenshtein + adjacent transposition),
  // bounded with early-exit so it stays cheap.
  function osa(a, b, max) {
    var al = a.length, bl = b.length;
    if (Math.abs(al - bl) > max) return max + 1;
    if (al === 0) return bl;
    if (bl === 0) return al;

    var prevprev = new Array(bl + 1);
    var prev = new Array(bl + 1);
    var cur = new Array(bl + 1);
    var i, j, tmp;

    for (j = 0; j <= bl; j++) prev[j] = j;

    for (i = 1; i <= al; i++) {
      cur[0] = i;
      var rowMin = i;
      var ac = a.charCodeAt(i - 1);
      for (j = 1; j <= bl; j++) {
        var cost = ac === b.charCodeAt(j - 1) ? 0 : 1;
        var v = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + cost);
        if (i > 1 && j > 1 &&
            ac === b.charCodeAt(j - 2) &&
            a.charCodeAt(i - 2) === b.charCodeAt(j - 1)) {
          v = Math.min(v, prevprev[j - 2] + 1);
        }
        cur[j] = v;
        if (v < rowMin) rowMin = v;
      }
      if (rowMin > max) return max + 1;
      tmp = prevprev; prevprev = prev; prev = cur; cur = tmp;
    }
    return prev[bl];
  }

  /* ----------------------------------------------------------- correction */
  function maxDistFor(len) {
    if (len <= 4) return 1;
    if (len <= 7) return 2;
    return 3;
  }

  function isPrefixOfVocab(tok) {
    var toks = VOCAB.tokens;
    for (var i = 0; i < toks.length; i++) {
      if (toks[i].length > tok.length && toks[i].lastIndexOf(tok, 0) === 0) return true;
    }
    return false;
  }

  function splitToken(tok) {
    // "missing space" compounds: canam -> can am, balljoint -> ball joint.
    if (tok.length < 5) return null;
    for (var i = 2; i <= tok.length - 2; i++) {
      var a = tok.slice(0, i), b = tok.slice(i);
      if ((a.length >= 3 || b.length >= 3) && VOCAB.freq[a] && VOCAB.freq[b]) {
        return a + ' ' + b;
      }
    }
    return null;
  }

  function correctToken(tok) {
    if (tok.length < 3) return tok;          // too short to judge
    if (/^\d+$/.test(tok)) return tok;       // pure numbers (years, sizes) - leave alone
    if (VOCAB.freq[tok]) return tok;         // already a real word
    if (isPrefixOfVocab(tok)) return tok;    // user is mid-typing a real word

    var split = splitToken(tok);             // missing-space compound
    if (split) return split;

    var maxD = maxDistFor(tok.length);
    var best = null, bestD = maxD + 1, bestFreq = -1;
    var toks = VOCAB.tokens, freq = VOCAB.freq;
    for (var i = 0; i < toks.length; i++) {
      var cand = toks[i];
      if (cand.length < 3) continue;
      if (cand.length <= 3 && tok.length >= 5) continue;  // don't collapse a long typo into a tiny word
      if (Math.abs(cand.length - tok.length) > maxD) continue;
      var d = osa(tok, cand, maxD);
      if (d <= maxD) {
        var f = freq[cand] || 0;
        if (d < bestD || (d === bestD && f > bestFreq)) {
          best = cand; bestD = d; bestFreq = f;
        }
      }
    }
    return best || tok;
  }

  function correct(query) {
    if (!VOCAB || !VOCAB.tokens.length) return { corrected: query, changed: false };
    var toks = tokenize(query);
    if (!toks.length) return { corrected: query, changed: false };
    var changed = false;
    var out = toks.map(function (t) {
      var c = correctToken(t);
      if (c !== t) changed = true;
      return c;
    });
    return { corrected: out.join(' '), changed: changed };
  }

  window.AxiomTypoSearch = { correct: correct, ready: loadVocab };

  /* ------------------------------------------------------------- helpers */
  function esc(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function fullSearchUrl(term, noCorrect) {
    var u = searchBase() + '?q=' + encodeURIComponent(term) + '&options%5Bprefix%5D=last';
    if (noCorrect) u += '&qc=1';
    return u;
  }

  /* -------------------------------------------- predictive dropdown patch */
  function fetchSuggestMarkup(term, signal) {
    // Keep these resource params in sync with assets/predictive-search.js —
    // without them Shopify returns query suggestions only (docs/search-dropdown.md).
    return fetch(
      predictiveUrl() +
        '?q=' +
        encodeURIComponent(term) +
        '&section_id=predictive-search' +
        '&resources[type]=product,query,collection,article&resources[limit]=4&resources[limit_scope]=each',
      { signal: signal }
    )
      .then(function (r) { if (!r.ok) throw new Error(r.status); return r.text(); })
      .then(function (text) {
        var node = new DOMParser().parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search');
        return node ? node.innerHTML : '';
      });
  }
  function markupHasResults(markup) {
    return /predictive-search__list-item/.test(markup);
  }
  function markupHasProducts(markup) {
    return /predictive-search-option-product/.test(markup);
  }

  /* ------------------------------------------------- catalogue vocabulary map
   * Shopify's predictive endpoint prefix-matches titles/type/vendor only — it
   * never reads the body copy the way the results page does. So industry terms
   * that don't appear in our product titles ("hub assembly", "half shaft")
   * return ZERO products in the dropdown even though the results page finds
   * dozens. This maps trade vocabulary onto the words our titles actually use.
   * Longest phrases first — the replacement is applied to the whole term.
   * See docs/search-dropdown.md.
   */
  var SYNONYMS = [
    ['hub bearing assembly', 'wheel bearing assembly'],
    ['wheel hub assembly', 'wheel bearing assembly'],
    ['hub assembly', 'wheel bearing assembly'],
    ['unit bearing', 'wheel bearing'],
    ['hub bearing', 'wheel bearing'],
    ['wheel hub', 'wheel bearing'],
    ['half shaft', 'cv axle'],
    ['halfshaft', 'cv axle'],
    ['axle shaft', 'cv axle'],
    ['cv shaft', 'cv axle'],
    ['drive axle', 'cv axle'],
    ['upper arm', 'upper control arm'],
    ['control arm', 'upper control arm'],
    ['uca', 'upper control arm'],
    ['torsion key', 'leveling key'],
    ['level kit', 'leveling kit'],
    ['lift kit', 'leveling kit'],
    ['coilover', 'shock'],
    ['strut', 'shock'],
    ['abs sensor', 'wheel speed sensor'],
    ['speed sensor', 'wheel speed sensor'],
    ['heim', 'heim joint'],
    ['hub', 'wheel bearing'],
  ];

  function applySynonyms(term) {
    var t = ' ' + String(term).toLowerCase().replace(/\s+/g, ' ').trim() + ' ';
    var changed = false;
    for (var i = 0; i < SYNONYMS.length; i++) {
      var from = ' ' + SYNONYMS[i][0] + ' ';
      if (t.indexOf(from) > -1) {
        t = t.split(from).join(' ' + SYNONYMS[i][1] + ' ');
        changed = true;
        break; // one substitution is enough; avoid compounding
      }
    }
    t = t.replace(/\s+/g, ' ').trim();
    return changed && t ? t : null;
  }
  function injectDropdownNote(markup, corrected, original, lead) {
    var note =
      '<div class="axiom-typo-note" role="status">' +
        '<span class="axiom-typo-note__main">' + (lead || 'Showing results for') + ' <strong>' + esc(corrected) + '</strong></span>' +
        '<a class="axiom-typo-note__alt" href="' + fullSearchUrl(original, true) + '">Search for &ldquo;' + esc(original) + '&rdquo; instead</a>' +
      '</div>';
    return markup.replace(/(<div id="predictive-search-results"[^>]*>)/, '$1' + note);
  }

  function enhancedGetSearchResults(searchTerm) {
    var self = this;
    var queryKey = searchTerm.replace(' ', '-').toLowerCase();

    self.setLiveRegionLoadingState();

    if (self.cachedResults[queryKey]) {
      self.renderSearchResults(self.cachedResults[queryKey]);
      return;
    }

    loadVocab(); // warm in the background

    var signal = self.abortController.signal;

    fetchSuggestMarkup(searchTerm, signal)
      .then(function (markup) {
        // Query suggestions but no PARTS is the common failure on this catalogue
        // (e.g. "hub assembly"). Retry once through the vocabulary map.
        if (markupHasResults(markup) && !markupHasProducts(markup)) {
          var syn = applySynonyms(searchTerm);
          if (syn && syn.toLowerCase() !== searchTerm.toLowerCase()) {
            return fetchSuggestMarkup(syn, signal).then(function (sm) {
              if (markupHasProducts(sm)) {
                return { markup: injectDropdownNote(sm, syn, searchTerm, 'Showing parts for') };
              }
              return { markup: markup };
            });
          }
        }
        if (markupHasResults(markup)) return { markup: markup };
        // Raw query found nothing at all -> try a spelling correction.
        return loadVocab().then(function () {
          var c = correct(searchTerm);
          if (!c.changed || c.corrected.toLowerCase() === searchTerm.toLowerCase()) {
            return { markup: markup };
          }
          return fetchSuggestMarkup(c.corrected, signal).then(function (cm) {
            if (markupHasResults(cm)) {
              return { markup: injectDropdownNote(cm, c.corrected, searchTerm) };
            }
            return { markup: markup };
          });
        });
      })
      .then(function (res) {
        self.allPredictiveSearchInstances.forEach(function (inst) {
          inst.cachedResults[queryKey] = res.markup;
        });
        self.renderSearchResults(res.markup);
      })
      .catch(function (error) {
        if (error && (error.code === 20 || error.name === 'AbortError')) return; // aborted
        self.close();
      });
  }

  function patchPredictiveSearch() {
    if (!window.customElements || !customElements.whenDefined) return;
    customElements.whenDefined('predictive-search').then(function () {
      var ctor = customElements.get('predictive-search');
      if (ctor && ctor.prototype && !ctor.prototype.__axiomPatched) {
        ctor.prototype.getSearchResults = enhancedGetSearchResults;
        ctor.prototype.__axiomPatched = true;
      }
    });
  }

  /* ------------------------------------------------ search results page */
  var SPARSE_MAX = 2; // correct when a query returns this few results or fewer

  function countResults(term) {
    var url = searchBase() + '?q=' + encodeURIComponent(term) + '&options%5Bprefix%5D=last';
    return fetch(url, { headers: { Accept: 'text/html' } })
      .then(function (r) { return r.ok ? r.text() : ''; })
      .then(function (html) {
        var grid = new DOMParser().parseFromString(html, 'text/html').querySelector('#product-grid');
        return grid ? grid.querySelectorAll('.grid__item').length : 0;
      })
      .catch(function () { return -1; });
  }

  function handleSearchPage() {
    var tpl = document.querySelector('.template-search');
    if (!tpl) return;

    var params = new URLSearchParams(location.search);
    var q = (params.get('q') || '').trim();
    if (!q) return;

    var from = params.get('axiom_from');
    if (from) { showPageBanner(tpl, q, from); return; }

    if (params.get('qc')) return;            // already attempted a correction

    var rawCount = tpl.querySelectorAll('#product-grid .grid__item').length;
    if (rawCount > SPARSE_MAX) return;       // plenty of results, leave it alone

    loadVocab().then(function () {
      var c = correct(q);
      if (!c.changed || c.corrected.toLowerCase() === q.toLowerCase()) return;
      // Only redirect if the corrected query is actually better than the raw one.
      countResults(c.corrected).then(function (correctedCount) {
        if (correctedCount > rawCount) {
          var u = new URL(location.href);
          u.searchParams.set('q', c.corrected);
          u.searchParams.set('options[prefix]', 'last');
          u.searchParams.set('qc', '1');
          u.searchParams.set('axiom_from', q);
          location.replace(u.toString());
        }
      });
    });
  }

  function showPageBanner(tpl, corrected, original) {
    if (tpl.querySelector('.axiom-typo-banner')) return;
    var header = tpl.querySelector('.template-search__header');
    if (!header) return;
    var div = document.createElement('div');
    div.className = 'axiom-typo-banner';
    div.setAttribute('role', 'status');
    div.innerHTML =
      'Showing results for <strong>' + esc(corrected) + '</strong> &middot; ' +
      '<a href="' + fullSearchUrl(original, true) + '">search instead for &ldquo;' + esc(original) + '&rdquo;</a>';
    header.appendChild(div);
  }

  /* ----------------------------------------------------------------- init */
  patchPredictiveSearch();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', handleSearchPage);
  } else {
    handleSearchPage();
  }
})();
